// Antworten der Lohn-API im Kanzlei-Dashboard.
export type LohnKind =
  | 'abrechnungen'
  | 'zahlungsliste'
  | 'beitragsnachweise'
  | 'lst_anmeldung'
  | 'lst_freigabe'
  | 'lohnjournal'
  | 'buchungsliste'
  | 'sonstige';
export type EmployeeStatus = 'neu' | 'aktiv';

export const LOHN_KINDS: { key: LohnKind; label: string }[] = [
  { key: 'abrechnungen', label: 'Lohnabrechnungen' },
  { key: 'zahlungsliste', label: 'Zahlungsliste' },
  { key: 'beitragsnachweise', label: 'Beitragsnachweise' },
  { key: 'lst_anmeldung', label: 'Lohnsteuer-Anmeldung' },
  { key: 'lst_freigabe', label: 'Lohnsteuer-Anmeldung zur Freigabe' },
  { key: 'lohnjournal', label: 'Lohnjournal' },
  { key: 'buchungsliste', label: 'Buchungsliste' },
  { key: 'sonstige', label: 'Weitere Lohnunterlagen' },
];
export const LOHN_KIND_LABEL = Object.fromEntries(LOHN_KINDS.map((k) => [k.key, k.label])) as Record<LohnKind, string>;

export interface EmployeeItem {
  id: string;
  name: string;
  job: string;
  kind: string;
  status: EmployeeStatus;
  entryDate: string;
  createdAt: string;
  openChanges: number;
  // Kündigung gemeldet: letzter Arbeitstag; gone = schon vorbei; abgemeldet = Kündigung erledigt
  termination: { lastDay: string; status: MeldungStatus } | null;
  gone: boolean;
  abgemeldet: boolean;
}

export type MeldungStatus = 'offen' | 'erledigt';

export interface EmployeeChange {
  id: string;
  what: string;
  value: string;
  validFrom: string | null;
  status: MeldungStatus;
  file: boolean;
  createdAt: string;
  createdBy: string | null;
  doneAt: string | null;
  doneBy: string | null;
}

export interface EmployeeTermination {
  id: string;
  by: string;
  receivedOn: string | null;
  lastDay: string;
  vacationDays: number | null;
  note: string | null;
  file: boolean;
  status: MeldungStatus;
  gone: boolean;
  createdAt: string;
  createdBy: string | null;
  doneAt: string | null;
  doneBy: string | null;
  lastDayOld: string | null;
  reason: string | null;
  correctedAt: string | null;
}

export interface EmployeeDetail extends Omit<EmployeeItem, 'createdAt' | 'openChanges' | 'termination' | 'gone'> {
  clientId: string;
  clientName: string;
  contactName: string;
  createdAt: string;
  createdBy: string | null;
  registeredAt: string | null;
  registeredBy: string | null;
  // übernommene Änderungen stehen schon drin, der alte Wert in note
  info: { label: string; value: string; note?: string }[];
  files: { kind: string; label: string; mime: string }[];
  changes: EmployeeChange[];
  termination: EmployeeTermination | null;
  // Wiedereinstellung: die alte Beschäftigung bzw. die neue Personalakte
  rehiredFrom: { id: string; entryDate: string; lastDay: string | null } | null;
  rehiredAs: { id: string; entryDate: string; status: 'neu' | 'aktiv' } | null;
  // Einverständnis zur Ausweiskopie: der Mitarbeiter über seinen Link oder der Arbeitgeber für ihn
  ausweisConsent: { by: 'mitarbeiter' | 'arbeitgeber'; at: string } | null;
}

export interface LohnDoc {
  id: string;
  year: number;
  month: number;
  kind: LohnKind;
  name: string;
  mime: string;
  createdAt: string;
  uploadedBy: string | null;
  // Seiten, die eine neuere Lohnabrechnung ersetzt: der Mandant sieht diese PDF ohne sie, hidden = gar nicht mehr
  korrektur: { names: string[]; at: string; hidden: boolean } | null;
  // nur bei der Lohnsteuer-Anmeldung zur Freigabe: null = wartet noch auf den Mandanten
  freigabe?: { at: string; name: string } | null;
}

export interface LohnTabResponse {
  client: { id: string; name: string; contactName: string };
  today: { year: number; month: number };
  // Mitarbeiterliste als PDF oder Foto: liest die KI; kiTest: übergangsweise nicht nur in der EU (nur Testdaten)
  ki: boolean;
  kiTest: boolean;
  // KI für diesen Mandanten ausgeschaltet
  kiAus: boolean;
  years: number[];
  employees: EmployeeItem[];
  docs: LohnDoc[];
}

export interface NewEmployee {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  entryDate: string;
  createdAt: string;
  createdBy: string | null;
  // nur für den Admin: Lohn-Zuständige(r), null = offen
  lohn?: string | null;
}

// Meldungen des Mandanten zu einem Mitarbeiter (Änderung oder Kündigung), bis „Erledigt“
interface Meldung {
  id: string;
  employeeId: string;
  clientId: string;
  clientName: string;
  name: string;
  createdAt: string;
  createdBy: string | null;
  lohn?: string | null;
}

export interface ChangeMeldung extends Meldung {
  what: string;
  value: string;
  validFrom: string | null;
}

export interface TerminationMeldung extends Meldung {
  by: string;
  lastDay: string;
}

export interface NewEmployeesResponse {
  admin: boolean;
  items: NewEmployee[];
  changes: ChangeMeldung[];
  terminations: TerminationMeldung[];
}

// Mitarbeiter übernehmen (Liste beim Mandantenwechsel)
export type ImportKind = 'text' | 'date' | 'num' | 'choice' | 'steuerId' | 'sv' | 'iban' | 'plz' | 'email';
export interface ImportField {
  key: string;
  label: string;
  kind: ImportKind;
  values: string[] | null;
  required: boolean;
}
export interface ImportCheck {
  errors: string[];
  warnings: string[];
  // schon in der App oder doppelt in der Liste
  dup: boolean;
  // schon in der App ohne Kündigung: übernommen wird nur der Austritt
  nachtragen: boolean;
}
export interface ImportRead {
  source: 'excel' | 'csv' | 'ki';
  fields: ImportField[];
  rows: (ImportCheck & { line: number; values: Record<string, string | number>; raw: Record<string, string> })[];
  used: string[];
  ignored: string[];
  skipped: number;
  max: number;
  hinweis: string | null;
}
